import { useState } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import client from '../api/client';

export default function SearchIntegrated() {
  const checkAuth = useAuthGuard();
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState<{cafe: any[], kin: any[], blog: any[]} | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async () => {
    if (!checkAuth()) return;
    if (!keyword.trim()) return;
    setLoading(true); setError(''); setResults(null);
    try {
      const [cafe, kin, blog] = await Promise.all([
        client.post('/search/cafe', { keyword }),
        client.post('/search/kin', { keyword }),
        client.post('/search/blog', { keyword }),
      ]);
      setResults({ cafe: cafe.data.urls, kin: kin.data.urls, blog: blog.data.urls });
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 429) {
        if (window.confirm(detail + '\n\n업그레이드하시겠습니까?')) {
          window.location.href = '/pricing';
        }
      } else {
        setError('조회 실패');
      }
    }
    finally { setLoading(false); }
  };

  const handleExcel = () => {
    if (!results) return;
    const rows = [['유형', 'URL', '카페ID', '게시글ID']];
    results.cafe.forEach((u: any) => rows.push(['카페', u.url, u.cafe_id, u.article_id]));
    results.kin.forEach((u: string) => rows.push(['지식인', u, '', '']));
    results.blog.forEach((u: string) => rows.push(['블로그', u, '', '']));
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `integrated_${keyword}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const s: any = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 16 },
    input: { flex: 1, background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 14px', color: '#E8ECF4', fontSize: 14 },
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 28px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    btnGhost: { background: 'none', border: '0.5px solid #2A3045', borderRadius: 6, padding: '7px 16px', color: '#8892A4', fontSize: 12, cursor: 'pointer' },
  };

  const SECTIONS = [
    { key: 'cafe', label: '카페', color: '#00D4AA', bg: '#0F2A22' },
    { key: 'kin', label: '지식인', color: '#378ADD', bg: '#0D1F35' },
    { key: 'blog', label: '블로그', color: '#F6AD55', bg: '#2A1F0D' },
  ];

  return (
    <Layout>
      <div style={{ maxWidth: 900 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#E8ECF4', fontSize: 18, fontWeight: 700 }}>통합검색 수집</div>
          <div style={{ color: '#8892A4', fontSize: 12, marginTop: 4 }}>키워드 하나로 카페·지식인·블로그 결과를 한 번에 확인합니다</div>
        </div>

        <div style={s.card}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              style={s.input}
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="키워드 입력"
            />
            <button style={s.btn} onClick={handleSearch} disabled={loading || !keyword.trim()}>
              {loading ? '수집 중...' : '조회'}
            </button>
            {results && <button style={s.btnGhost} onClick={handleExcel}>엑셀 다운로드</button>}
          </div>
          {error && <div style={{ color: '#F56565', fontSize: 13, marginTop: 8 }}>{error}</div>}
        </div>

        {results && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {SECTIONS.map(sec => {
              const urls: any[] = results[sec.key as keyof typeof results];
              return (
                <div key={sec.key} style={s.card}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ background: sec.bg, color: sec.color, fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 99 }}>{sec.label}</span>
                      <span style={{ color: '#4A5568', fontSize: 12 }}>{urls.length}건</span>
                    </div>
                    {urls.length > 0 && (
                      <button style={s.btnGhost} onClick={() => {
                        const lines = urls.map((u: any) => typeof u === 'string' ? u : u.url);
                        navigator.clipboard.writeText(lines.join('\n'));
                      }}>복사</button>
                    )}
                  </div>
                  {urls.length === 0
                    ? <div style={{ color: '#4A5568', fontSize: 12 }}>결과 없음</div>
                    : urls.map((u: any, i: number) => {
                        const url = typeof u === 'string' ? u : u.url;
                        return (
                          <div key={i} style={{ padding: '5px 0', borderBottom: '0.5px solid #1E2535' }}>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                              <span style={{ color: '#4A5568', fontSize: 10, paddingTop: 2, flexShrink: 0 }}>{i+1}</span>
                              <a href={url} target="_blank" rel="noreferrer" style={{ color: '#8892A4', fontSize: 11, wordBreak: 'break-all', lineHeight: 1.5 }}>{url}</a>
                            </div>
                          </div>
                        );
                      })
                  }
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
